import { Flex, Group, Stack, Tabs, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { createFileRoute } from "@tanstack/react-router";
import { IconPlus } from "@tabler/icons-react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { AddSupplierModal } from "@/components/features/Procurement/Accounts/AddSupplierModal";
import { SuppliersTable } from "@/components/features/Procurement/Accounts/SuppliersTable";
import { VendorAccountsTable } from "@/components/features/Procurement/Accounts/VendorAccountsTable";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

import styles from "./procurement.module.scss";

const ACCOUNTS_TABS = [
  { value: "accounts", label: "Supplier Accounts" },
  { value: "suppliers", label: "Suppliers" },
] as const;

export const Route = createFileRoute("/_authenticated/procurement/accounts")({
  component: VendorAccountsPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function VendorAccountsPage() {
  const { entityName } = useActiveSession();

  useBreadcrumb([
    {
      title: "Procurement",
      href: "/procurement",
      icon: getRouteIcon(MODULE_KEYS_MAP.procurement),
    },
    { title: "Supplier Accounts", href: "/procurement/accounts" },
  ]);

  const { hasAccess } = usePermissions();
  const { edit: canEdit, create: canCreate } = hasAccess({ module: MODULE_KEYS_MAP.procurement });
  const [isAddSupplierOpen, addSupplierHandlers] = useDisclosure(false);
  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: ACCOUNTS_TABS[0].value });

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Group gap="var(--spacing-small)" align="baseline">
          <Title order={3} className={styles.title}>
            Supplier Accounts
          </Title>
          {entityName && <Text c="var(--text-weak)">{entityName}</Text>}
        </Group>
        {canCreate && (
          <Button
            variant="primary"
            iconLeft={<IconPlus size={16} />}
            onClick={addSupplierHandlers.open}
          >
            Add Supplier
          </Button>
        )}
      </Group>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value)} className={styles.tab}>
        <Tabs.List className={styles.tabsList}>
          {ACCOUNTS_TABS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="accounts" className={styles.tabPanel}>
          <Stack gap="var(--spacing-large)" className={styles.tableCard}>
            <VendorAccountsTable canEdit={canEdit} canCreate={canCreate} />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="suppliers" className={styles.tabPanel}>
          <Stack gap="var(--spacing-large)" className={styles.tableCard}>
            <SuppliersTable canCreate={canCreate} />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <AddSupplierModal isOpen={isAddSupplierOpen} onClose={addSupplierHandlers.close} />
    </Flex>
  );
}
